<script setup lang="ts">
/**
 * UI-kit component
 * Category: Tables
 * Name: AppTable
 */
import type { AppTableProps } from '../types/interfaces'

import { AppPagination } from '@shared/ui/paginations'

import { useAppTable, DEFAULT_APP_TABLE_META } from '../composables/use-app-table.hook'

defineOptions({
  name: 'AppTable',
})

withDefaults(defineProps<AppTableProps>(), {
  meta: () => ({ ...DEFAULT_APP_TABLE_META }),
  loading: false,
  fixed: false,
  paginated: true,
  perPageOptions: () => [15, 25, 50, 100, 150],
})

const emit = defineEmits<{
  'change:page': [page: number]
  'change:per-page': [perPage: number]
}>()

const {
  handleChangePage,
  handleChangePerPage,
} = useAppTable({
  onChangePage: page => emit('change:page', page),
  onChangePerPage: perPage => emit('change:per-page', perPage),
})
</script>

<template>
  <div class="app-table">
    <AppPagination
      v-if="paginated"
      position="top"
      :meta="meta"
      :disabled="loading"
      :per-page-options="perPageOptions"
      @change:page="handleChangePage"
      @change:per-page="handleChangePerPage"
    />
    <div
      v-if="$slots.summary"
      class="app-table__summary"
    >
      <slot name="summary" />
    </div>
    <div class="overflow-y-scroll">
      <table :class="['app-table__box', { fixed }]">
        <thead
          v-if="$slots.head"
          class="app-table__head"
        >
          <slot name="head" />
        </thead>
        <tbody
          v-if="$slots.body"
          class="app-table__body"
        >
          <slot name="body" />
        </tbody>
      </table>
    </div>
    <div
      v-if="$slots.total"
      class="app-table__total"
    >
      <slot name="total" />
    </div>
    <AppPagination
      v-if="paginated"
      position="bottom"
      :meta="meta"
      :disabled="loading"
      :per-page-options="perPageOptions"
      @change:page="handleChangePage"
      @change:per-page="handleChangePerPage"
    />
  </div>
</template>

<style lang="scss" scoped src="../styles/_app-table.scss" />
